/**
 * The shell: who is signed in, which tab is open, and what the server can do.
 *
 * The three tabs are the three things a collector does - file cards (Scan), put
 * boxes on shelves (Bins), pull cards out (Pulls) - and only the first needs a
 * session open, so the tabs are not a wizard.
 *
 * The API arrives as a prop rather than being imported by each screen, which is
 * what lets a test drive the whole loop against a fake and a browser run it
 * against the real thing.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ArtIndex } from '@unsorted/shared';
import { createHttpApi, type Api } from './api/http.js';
import { SAME_ORIGIN } from './api/origin.js';
import type { Container, Meta, User } from './api/types.js';
import { Banner } from './components/Banner.js';
import { CollectionScreen } from './screens/CollectionScreen.js';
import { IntakeScreen } from './screens/IntakeScreen.js';
import { PicklistsScreen } from './screens/PicklistsScreen.js';
import { ProfileScreen } from './screens/ProfileScreen.js';
import { SettingsScreen } from './screens/SettingsScreen.js';
import { SignInScreen } from './screens/SignInScreen.js';
import { StorageScreen } from './screens/StorageScreen.js';
import { UserMenu, type AccountDestination } from './components/UserMenu.js';
import { useAsync } from './state/useAsync.js';
import { installedArtIndexFromBridge } from './ota/art-index.js';
import { capacitorBridge, type CapacitorGlobals } from './ota/host.js';
import type { CaptureSource } from './scan/capture.js';
import { printingLabel } from './scan/queue.js';

export interface AppProps {
  /** Injected in tests: the whole API, in memory. */
  api?: Api;
  /** Injected in tests, where there is no canvas to decode a capture with. */
  capture?: CaptureSource;
  /**
   * The art index installed on this device (ADR 0011), for scans the catalog cannot
   * answer.
   *
   * Defaults to reading the artifact out of the shell's storage, which is nothing in a
   * browser tab; injected in tests, where an index can be handed to the surface
   * without a device.
   */
  localArtIndex?: () => Promise<ArtIndex | null>;
  /**
   * Injected in tests: the Capacitor global, which is what tells an installed app
   * from a browser tab. Read here rather than by each screen, because two things
   * depend on it - the installed art index, and whether the app advertises itself.
   */
  capacitor?: CapacitorGlobals;
  /**
   * The server the app talks to, when it is not this origin: named in the APK and
   * read at launch by `main.tsx`. `''` - the default - is a browser tab, where the
   * API is served by whatever served the page.
   */
  apiBaseUrl?: string;
}

type Tab = 'scan' | 'bins' | 'collection' | 'pulls' | AccountDestination;

const TAB_HASH: Record<Tab, string> = {
  scan: '#/scan',
  bins: '#/bins',
  collection: '#/collection',
  pulls: '#/pulls',
  profile: '#/profile',
  settings: '#/settings',
};

function tabFromHash(): Tab {
  const hash = window.location.hash.toLowerCase();
  if (hash.startsWith('#/bins')) return 'bins';
  if (hash.startsWith('#/collection')) return 'collection';
  if (hash.startsWith('#/pulls')) return 'pulls';
  if (hash.startsWith('#/profile')) return 'profile';
  if (hash.startsWith('#/settings')) return 'settings';
  return 'scan';
}

type SessionState =
  | { status: 'checking' }
  | { status: 'signed-out'; reason?: string }
  | { status: 'signed-in'; user: User };

type MainTab = Exclude<Tab, AccountDestination>;

const TABS: ReadonlyArray<{ id: MainTab; label: string; hint: string }> = [
  { id: 'scan', label: 'Scan', hint: 'File cards into a bin' },
  { id: 'bins', label: 'Bins', hint: 'Boxes, binders and drawers' },
  { id: 'collection', label: 'Cards', hint: 'Every filed card, searchable' },
  { id: 'pulls', label: 'Pulls', hint: 'Walk a pick list' },
];

const TAB_ICONS: Record<MainTab, React.ReactNode> = {
  scan: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8V5.5A2.5 2.5 0 0 1 5.5 3H8" />
      <path d="M16 3h2.5A2.5 2.5 0 0 1 21 5.5V8" />
      <path d="M21 16v2.5a2.5 2.5 0 0 1-2.5 2.5H16" />
      <path d="M8 21H5.5A2.5 2.5 0 0 1 3 18.5V16" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  ),
  bins: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 8 12 3.5 20.5 8v8L12 20.5 3.5 16Z" />
      <path d="M3.5 8 12 12.5 20.5 8" />
      <path d="M12 12.5v8" />
    </svg>
  ),
  collection: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="5" width="13" height="14" rx="2.5" />
      <path d="M8 5V4.5A1.5 1.5 0 0 1 9.5 3h9A1.5 1.5 0 0 1 20 4.5v13" />
    </svg>
  ),
  pulls: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6.5h12" />
      <path d="M4 12h12" />
      <path d="M4 17.5h7" />
      <path d="m16.5 17 1.8 1.8 3.2-3.6" />
    </svg>
  ),
};

export function App({ api: injected, capture, localArtIndex, capacitor, apiBaseUrl = SAME_ORIGIN }: AppProps = {}) {
  const api = useMemo(() => injected ?? createHttpApi({ baseUrl: apiBaseUrl }), [injected, apiBaseUrl]);
  // Non-null only inside the installed app; null in a browser tab, and in jsdom.
  const inShell = useMemo(() => capacitorBridge(capacitor) !== null, [capacitor]);
  // The artifact's *version* is the updater's business and is reported at launch; what
  // a scan needs is the fingerprints.
  const readLocalArtIndex = useMemo(
    () => localArtIndex ?? (async () => (await installedArtIndexFromBridge(capacitorBridge(capacitor)))?.index ?? null),
    [localArtIndex, capacitor],
  );
  const [session, setSession] = useState<SessionState>({ status: 'checking' });
  const [tab, setTabState] = useState<Tab>(() => {
    try {
      return tabFromHash();
    } catch {
      return 'scan';
    }
  });
  const setTab = (next: Tab): void => {
    setTabState(next);
    try {
      if (window.location.hash !== TAB_HASH[next]) window.location.hash = TAB_HASH[next];
    } catch {
      // Hash sync is a nicety (deep-linkable tabs); never break a tab switch over it.
    }
  };
  useEffect(() => {
    const onHash = (): void => {
      try {
        setTabState(tabFromHash());
      } catch {
        // ignore
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  /** What this session filed, so "pull those back out" needs no retyping. */
  const [filed, setFiled] = useState<Array<{ printingId: number; name: string; setCode: string; collectorNumber: string }>>([]);
  const [meta, reloadMeta] = useAsync<Meta>(() => api.meta(), [api]);
  // The storage tree lives here, not in each screen: a container added on the
  // Bins tab has to show up on the Scan tab without a reload, and two screens
  // fetching the same list is how they end up disagreeing.
  const [containers, reloadContainers] = useAsync<Container[]>(() => api.containers(), [api, session.status]);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((user) => {
        if (!cancelled) setSession({ status: 'signed-in', user });
      })
      .catch(() => {
        // A 401 is the ordinary "not signed in yet"; anything else is reported by
        // the screen that needs the API, so this stays quiet.
        if (!cancelled) setSession({ status: 'signed-out' });
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (session.status === 'checking') {
    return (
      <main className="shell" id="main">
        <p role="status" className="hint" aria-live="polite">
          Checking your session…
        </p>
      </main>
    );
  }

  if (session.status === 'signed-out') {
    return (
      <main className="shell" id="main">
        <a className="skip-link" href="#signin-form">
          Skip to sign in
        </a>
        <SignInScreen
          api={api}
          meta={meta.status === 'ready' ? meta.value : null}
          onSignedIn={(user) => setSession({ status: 'signed-in', user })}
        />
      </main>
    );
  }

  const signOut = () => {
    void api
      .signOut()
      .catch(() => undefined)
      .finally(() => setSession({ status: 'signed-out' }));
  };

  const binCount = containers.status === 'ready' ? containers.value.reduce((total, c) => total + c.bins.length, 0) : 0;
  const tabBadge = (id: Tab): number | null => {
    if (id === 'pulls') return filed.length > 0 ? filed.length : null;
    if (id === 'bins') return binCount > 0 ? binCount : null;
    return null;
  };

  return (
    <main className="shell" id="main">
      <a className="skip-link" href="#panel">
        Skip to content
      </a>
      <header className="topbar">
        <div className="who">
          <div className="wordmark-row">
            <div>
              <h1 translate="no" className="wordmark">Unsorted</h1>
              <p className="hint">
                <span className="presence">ready to file</span>
              </p>
            </div>
          </div>
        </div>
        <UserMenu user={session.user} onNavigate={(destination) => setTab(destination)} onSignOut={signOut} />
      </header>

      {meta.status === 'error' && (
        <Banner kind="error" onRetry={reloadMeta}>
          The API did not answer: {meta.message}
        </Banner>
      )}
      {meta.status === 'ready' && !meta.value.recognition.available && (
        <Banner kind="warning">
          This server cannot recognise a capture. {meta.value.recognition.missing.join(' ')} Cards can still be filed by
          their set code and number.
        </Banner>
      )}

      <section className="panel" id="panel" hidden={tab !== 'scan'} aria-label="Scan">
        <IntakeScreen
          api={api}
          meta={meta.status === 'ready' ? meta.value : null}
          containers={containers}
          reloadContainers={reloadContainers}
          capture={capture}
          localArtIndex={readLocalArtIndex}
          onFiled={(printings) => setFiled((current) => [...printings, ...current].slice(0, 200))}
        />
      </section>
      <section className="panel" id="panel-bins" hidden={tab !== 'bins'} aria-label="Bins">
        <StorageScreen
          api={api}
          meta={meta.status === 'ready' ? meta.value : null}
          containers={containers}
          reloadContainers={reloadContainers}
        />
      </section>
      <section className="panel" id="panel-collection" hidden={tab !== 'collection'} aria-label="Collection">
        <CollectionScreen api={api} />
      </section>
      <section className="panel" id="panel-pulls" hidden={tab !== 'pulls'} aria-label="Pulls">
        <PicklistsScreen api={api} filed={filed} onPulled={() => setTab('pulls')} />
      </section>
      <section className="panel" id="panel-profile" hidden={tab !== 'profile'} aria-label="Profile">
        <ProfileScreen api={api} user={session.user} onSignOut={signOut} />
      </section>
      <section className="panel" id="panel-settings" hidden={tab !== 'settings'} aria-label="Settings">
        <SettingsScreen meta={meta.status === 'ready' ? meta.value : null} />
      </section>

      <nav className="tabs" aria-label="Sections">
        {TABS.map((entry) => {
          const badge = tabBadge(entry.id);
          return (
            <button
              key={entry.id}
              type="button"
              className={entry.id === tab ? 'tab current' : 'tab'}
              aria-current={entry.id === tab ? 'page' : undefined}
              title={entry.hint}
              onClick={() => setTab(entry.id)}
            >
              {TAB_ICONS[entry.id]}
              <span>{entry.label}</span>
              {badge !== null && (
                <span className="tab-badge" aria-hidden="true">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <footer>
        <p>
          Card data and images from <a href="https://scryfall.com">Scryfall</a>. Magic: The Gathering is a trademark of
          Wizards of the Coast. This is unofficial Fan Content and is not endorsed by Wizards.
          {/* Not offered to the app that already is the APK. */}
          {!inShell && (
            <>
              {' '}
              <a href="/api/downloads/apk">Android APK</a>
            </>
          )}
        </p>
        {filed.length > 0 && <p className="hint">Filed this session: {filed.slice(0, 3).map(printingLabel).join(', ')}
          {filed.length > 3 ? ` and ${filed.length - 3} more` : ''}.</p>}
      </footer>
    </main>
  );
}
